import {
  differenceInMinutes,
  format,
  formatDistanceToNow,
  parseISO,
} from "date-fns";
import { defineStore } from "pinia";
import { ref } from "vue";

import type { SnackbarState } from "@/types/stores";
import type { Timestamp } from "@bufbuild/protobuf/wkt";
import type { User } from "@/gen/tempus/v1/tempus_pb";

const DEFAULT_SNACKBAR: SnackbarState = {
  show: false,
  text: "",
  color: "success",
  timeout: 3000,
};

export type DateInput = string | Date | Timestamp | null | undefined;

function resolveDate(value: DateInput): Date | null {
  if (!value) {
    return null;
  }

  // Handle Timestamp objects
  if (typeof value === 'object' && 'seconds' in value) {
    return new Date(Number(value.seconds) * 1000);
  }

  return typeof value === "string" ? parseISO(value) : value;
}

export const useAppStore = defineStore("app", () => {
  const isLoading = ref(false);
  const snackbar = ref<SnackbarState>({ ...DEFAULT_SNACKBAR });

  const currentUser = ref<User | null>(null);
  // Whether the session cookie has been verified at least once this page load.
  const authChecked = ref(false);

  function formatDate(value: DateInput): string {
    const date = resolveDate(value);
    if (!date) {
      return "";
    }

    try {
      return format(date, "MMM d, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  }

  function formatDateTime(value: DateInput): string {
    const date = resolveDate(value);
    if (!date) {
      return "";
    }

    try {
      return format(date, "MMM d, yyyy h:mm a");
    } catch (error) {
      console.error("Error formatting date time:", error);
      return "";
    }
  }

  function formatTimeAgo(value: DateInput): string {
    const date = resolveDate(value);
    if (!date) {
      return "";
    }

    try {
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error("Error formatting time ago:", error);
      return "";
    }
  }

  function formatDuration(start: DateInput, end: DateInput): string {
    const startDate = resolveDate(start);
    const endDate = resolveDate(end);

    if (!startDate || !endDate) {
      return "";
    }

    try {
      const minutes = differenceInMinutes(endDate, startDate);
      return formatMinutes(minutes);
    } catch (error) {
      console.error("Error calculating duration:", error);
      return "";
    }
  }

  function formatMinutes(minutes: number | null | undefined): string {
    if (minutes === undefined || minutes === null || Number.isNaN(minutes)) {
      return "0min";
    }

    const rounded = Math.round(minutes * 10) / 10;

    if (rounded < 60) {
      return `${rounded}min`;
    }

    const hours = Math.floor(rounded / 60);
    const remainingMinutes = Math.round((rounded % 60) * 10) / 10;

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}min`;
  }

  function showSnackbar(
    text: string,
    color: SnackbarState["color"] = "success",
    timeout = 3000,
  ): void {
    snackbar.value = { show: true, text, color, timeout };
  }

  function hideSnackbar(): void {
    snackbar.value.show = false;
  }

  function showSuccessMessage(text: string): void {
    showSnackbar(text, "success");
  }

  function showErrorMessage(text: string): void {
    showSnackbar(text, "error", 5000);
  }

  function showWarningMessage(text: string): void {
    showSnackbar(text, "warning", 4000);
  }

  function showInfoMessage(text: string): void {
    showSnackbar(text, "info");
  }

  function setCurrentUser(user: User | null): void {
    currentUser.value = user;
    authChecked.value = true;
  }

  function clearCurrentUser(): void {
    currentUser.value = null;
  }

  function isAuthenticated(): boolean {
    return currentUser.value !== null;
  }

  return {
    isLoading,
    snackbar,
    currentUser,
    authChecked,
    formatDate,
    formatDateTime,
    formatTimeAgo,
    formatDuration,
    formatMinutes,
    showSnackbar,
    hideSnackbar,
    showSuccessMessage,
    showErrorMessage,
    showWarningMessage,
    showInfoMessage,
    setCurrentUser,
    clearCurrentUser,
    isAuthenticated,
  };
});
